const DEMO_ACCESS_ROUTE = 'demo-access';

import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
  NavigationGuardNext,
} from "vue-router";
import { useAuthStore } from "../stores/authStore";

import AuthLayout from "../layouts/AuthLayout.vue";
import LandingLayout from "../layouts/LandingLayout.vue";
import PublicSiteLayout from "../layouts/PublicSiteLayout.vue";

// Import du service de tracking des funnels
import { funnelService } from "../services/funnel.service";
import { posthogService } from "../services/posthog.service";
import { gtmService } from "../services/gtm.service";
import {
  getFunnelMappingForRoute,
  extractRouteData,
} from "../utils/route-funnel.mapping";
import { managerRoutes } from "./routes/managerRoutes";
import { adminRoutes } from "./routes/adminRoutes";
import { managerGuard, validateRouteParams } from "./routerHelpers";

const LANDING_ROUTE_NAME = "landing";
const NOT_FOUND_ROUTE_NAME = "404";
const FORBIDDEN_ROUTE_NAME = "forbidden";
const BILLING_ROUTE_NAME = "billing";
const PAYOUT_SETTINGS_ROUTE_NAME = "payout-settings";
const USERS_ROUTE_NAME = "users";
const CUSTOMERS_ROUTE_NAME = "customers";
const LAKES_ROUTE_NAME = "lakes";
const LAKES_LIST_ROUTE_NAME = "lakes-list";
const LAKE_MANAGE_ROUTE_NAME = "lake-manage";
const MANAGER_DASHBOARD_ROUTE_NAME = "manager-dashboard";
const ADMIN_DASHBOARD_ROUTE_NAME = "admin-dashboard";
const AUTH_REDIRECT_NAME = "login";
const ONBOARDING_BASE_ROUTE_NAME = "onboarding";
const LAKES_ROUTE_NAMES = new Set([
  LAKES_ROUTE_NAME,
  LAKES_LIST_ROUTE_NAME,
  LAKE_MANAGE_ROUTE_NAME,
  BILLING_ROUTE_NAME,
  PAYOUT_SETTINGS_ROUTE_NAME,
  USERS_ROUTE_NAME,
  CUSTOMERS_ROUTE_NAME,
]);
const PUBLIC_ROUTE_NAMES = new Set([
  LANDING_ROUTE_NAME,
  NOT_FOUND_ROUTE_NAME,
  FORBIDDEN_ROUTE_NAME,
]);
const PUBLIC_ROUTE_PREFIXES = ["/auth", "/public/", "/legal/"];
const ONBOARDING_ROUTE_NAMES = new Set([
  ONBOARDING_BASE_ROUTE_NAME,
  "logout",
]);
const STRIPE_ONBOARDING_PATH = "/manager/payout-settings";
const STRIPE_ONBOARDING_HASH = "#stripe";
const SESSION_KEYS = {
  ONBOARDING_COMPLETED: "onboarding_just_completed",
  PROFILE_REFETCHED: "profile_refetched_once",
  PROFILE_LAST_REFETCH: "profile_last_refetch",
};

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: LandingLayout,
    children: [
      {
        path: "",
        name: LANDING_ROUTE_NAME,
        component: () => import("../pages/landing/LandingPage.vue"),
        meta: { public: true },
      },
      {
        path: "/legal/:page",
        name: "legal",
        component: () => import("../pages/LegalPage.vue"),
        props: true,
        meta: { public: true },
        beforeEnter: (to, from, next) => {
          // 🔐 SÉCURITÉ: Validation du paramètre page pour éviter path traversal
          const allowedPages = ["cgv", "mentions", "cgu", "confidentialite"];
          if (
            typeof to.params.page === "string" &&
            allowedPages.includes(to.params.page)
          ) {
            next();
          } else {
            next({ name: "404" });
          }
        },
      },
      {
        path: "/legal/cgv",
        name: "cgv",
        component: () => import("../pages/legal/CgvPage.vue"),
        meta: { public: true },
      },
      {
        path: "/legal/mentions",
        name: "mentions",
        component: () => import("../pages/legal/MentionsPage.vue"),
        meta: { public: true },
      },
      {
        path: "/legal/cgu",
        name: "terms",
        component: () => import("../pages/legal/TermsPage.vue"),
        meta: { public: true },
      },
      {
        path: "/legal/confidentialite",
        name: "privacy",
        component: () => import("../pages/legal/PrivacyPage.vue"),
        meta: { public: true },
      },
    ],
  },
  // Onboarding dedicated full-screen routes (no sidebar)
  {
    name: ONBOARDING_BASE_ROUTE_NAME,
    path: "/onboarding",
    component: () => import("../pages/manager/onboarding/OnboardingPage.vue"),
    beforeEnter: managerGuard,
  },
  ...managerRoutes,
  ...adminRoutes,
  {
    // No PublicSiteLayout here: that layout renders PublicSiteHeader/PublicSiteFooter,
    // which require an `orgSlug` route param this page doesn't have (crashes on mount).
    // FisherLandingPage is self-sufficient (its own FisherHeader/FisherFooter).
    name: "fisher-landing",
    path: "/pecher",
    component: () => import("../pages/fisher/FisherLandingPage.vue"),
    meta: { public: true },
  },
  {
    path: "/lacs",
    component: PublicSiteLayout,
    meta: { public: true },
    children: [
      {
        name: "public-lakes-marketplace",
        path: "",
        component: () => import("../pages/public/PublicLakesPage.vue"),
        props: { marketplaceMode: true },
        meta: { public: true },
      },
    ],
  },
  // Routes publiques pour les réservations (mini-site)
  {
    name: "public",
    path: "/public/:orgSlug",
    component: PublicSiteLayout,
    beforeEnter: (to, from, next) => {
      // 🔐 SÉCURITÉ: Validation du slug d'organisation
      if (!validateRouteParams(to)) {
        return next({ name: "404" });
      }
      next();
    },
    children: [
      {
        // The org root (/public/{org}/) has no content of its own — without
        // this redirect it rendered the layout with an empty RouterView,
        // i.e. a blank page between header and footer.
        path: "",
        redirect: (to) => ({
          name: "public-lakes",
          params: { orgSlug: to.params.orgSlug },
        }),
      },
      {
        name: "public-lakes",
        path: "lakes",
        component: () => import("../pages/public/PublicLakesPage.vue"),
        meta: { public: true },
      },
      {
        name: "public-lake-details",
        path: "lakes/:lakeId",
        component: () => import("../pages/public/PublicLakeDetailsPage.vue"),
        props: true,
        meta: { public: true },
        beforeEnter: (to, from, next) => {
          if (!validateRouteParams(to)) {
            return next({ name: "404" });
          }
          next();
        },
      },
      {
        name: "public-reservation",
        path: "lakes/:lakeId/reserve",
        component: () => import("../pages/public/PublicReservationPage.vue"),
        props: true,
        meta: { public: true },
        beforeEnter: (to, from, next) => {
          if (!validateRouteParams(to)) {
            return next({ name: "404" });
          }
          next();
        },
      },
      {
        name: "public-reservation-success",
        path: "reservations/success",
        component: () => import("../pages/public/ReservationSuccessPage.vue"),
        meta: { public: true },
      },
      {
        name: "public-reservation-cancel",
        path: "reservations/cancel",
        component: () => import("../pages/public/ReservationCancelPage.vue"),
        meta: { public: true },
      },
      {
        name: "public-org-cgv",
        path: "cgv",
        component: () => import("../pages/public/PublicOrgCgvPage.vue"),
        props: true,
        meta: { public: true },
      },
      {
        name: "public-org-mentions",
        path: "mentions-legales",
        component: () => import("../pages/public/PublicOrgMentionsPage.vue"),
        props: true,
        meta: { public: true },
      },
    ],
  },
  {
    path: "/auth",
    component: AuthLayout,
    meta: { public: true },
    children: [
      {
        name: "login",
        path: "login",
        component: () => import("../pages/auth/Login.vue"),
        meta: { public: true, guestOnly: true },
      },
      {
        name: "signup",
        path: "signup",
        component: () => import("../pages/auth/Signup.vue"),
        meta: { public: true, guestOnly: true },
      },
      {
        name: "oauth-callback",
        path: "oauth-callback",
        component: () => import("../pages/auth/OauthCallbackPage.vue"),
        meta: { public: true },
      },
      {
        name: DEMO_ACCESS_ROUTE,
        path: DEMO_ACCESS_ROUTE,
        component: () => import("../pages/auth/DemoAccessRedirect.vue"),
        meta: { public: true },
      },
      {
        name: "forgot-password",
        path: "forgot-password",
        component: () => import("../pages/auth/RequestPasswordReset.vue"),
        meta: { public: true, guestOnly: true },
      },
      {
        name: "reset-password",
        path: "reset-password",
        component: () => import("../pages/auth/ResetPassword.vue"),
        meta: { public: true, guestOnly: true },
      },
      {
        name: "recover-password-email",
        path: "recover-password-email",
        component: () => import("../pages/auth/CheckTheEmail.vue"),
        meta: { public: true, guestOnly: true },
      },
      {
        path: "",
        redirect: { name: "login" },
      },
    ],
  },
  {
    path: "/demo-access",
    redirect: {
      name: DEMO_ACCESS_ROUTE,
    },
    meta: { public: true },
  },
  {
    name: NOT_FOUND_ROUTE_NAME,
    path: "/404",
    component: () => import("../pages/404.vue"),
    meta: { public: true },
    beforeEnter: (to, _from, next) => {
      const token = typeof to.query.token === "string" ? to.query.token.trim() : "";
      if (token) {
        return next({
          name: DEMO_ACCESS_ROUTE,
          query: { token },
        });
      }
      next();
    },
  },
  {
    name: FORBIDDEN_ROUTE_NAME,
    path: "/403",
    component: () => import("../pages/Forbidden.vue"),
    meta: { public: true },
  },
  {
    // 🔐 SÉCURITÉ: Catch-all route sécurisée
    path: "/:pathMatch(.*)*",
    redirect: { name: "404" },
  },
];

const STORAGE_ENABLED = () => typeof sessionStorage !== "undefined";

const getSessionItem = (key: string): string | null => {
  if (!STORAGE_ENABLED()) return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSessionItem = (key: string, value: string) => {
  if (!STORAGE_ENABLED()) return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore sessionStorage failures
  }
};

const removeSessionItem = (key: string) => {
  if (!STORAGE_ENABLED()) return;

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore sessionStorage failures
  }
};

const isStripeReturnRoute = (to: RouteLocationNormalized): boolean =>
  to.path.startsWith(STRIPE_ONBOARDING_PATH) &&
  to.query?.onboarding === "success";

const isPublicRouteCandidate = (
  to: RouteLocationNormalized,
  stripeReturn: boolean,
): boolean =>
  stripeReturn ||
  Boolean(to.meta?.public) ||
  PUBLIC_ROUTE_NAMES.has(String(to.name)) ||
  PUBLIC_ROUTE_PREFIXES.some((prefix) => to.path.startsWith(prefix));

const guestRedirectName = (authStore: ReturnType<typeof useAuthStore>) => {
  if (authStore.isSuperAdmin) return ADMIN_DASHBOARD_ROUTE_NAME;
  if (authStore.isManager) return MANAGER_DASHBOARD_ROUTE_NAME;
  return LANDING_ROUTE_NAME;
};

const shouldEnforceOnboarding = (
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>,
  justCompleted: boolean,
  onboardingSuccess: boolean,
): boolean => {
  if (!authStore.isAuthenticated || !authStore.user) return false;
  if (
    authStore.user.organization_is_demo ||
    authStore.user.organization_slug === "demo-etang"
  ) {
    return false;
  }
  if (
    authStore.user.organization_is_setup_complete ||
    justCompleted ||
    onboardingSuccess
  ) {
    return false;
  }
  return !ONBOARDING_ROUTE_NAMES.has(String(to.name));
};

const refreshProfileIfNeeded = async (authStore: ReturnType<
  typeof useAuthStore
>) => {
  const alreadyRefetched =
    getSessionItem(SESSION_KEYS.PROFILE_REFETCHED) === "1";
  const lastRefetchRaw = getSessionItem(SESSION_KEYS.PROFILE_LAST_REFETCH);
  const lastRefetch = lastRefetchRaw ? parseInt(lastRefetchRaw, 10) : null;
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  if (
    !alreadyRefetched ||
    (lastRefetch !== null && now - lastRefetch > fiveMinutes)
  ) {
    await authStore.fetchUserProfile();
    setSessionItem(SESSION_KEYS.PROFILE_REFETCHED, "1");
    setSessionItem(SESSION_KEYS.PROFILE_LAST_REFETCH, now.toString());
  }
};

const handleGuestOnlyRoute = (
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>,
  next: NavigationGuardNext,
): boolean => {
  if (!to.meta?.guestOnly || !authStore.isAuthenticated) return false;
  next({ name: guestRedirectName(authStore) });
  return true;
};

const ONBOARDING_MANUAL_MODE_KEY = "onboarding_manual_mode";

const isOnboardingManualMode = () =>
  getSessionItem(ONBOARDING_MANUAL_MODE_KEY) === "1";

const isOnboardingAccessAllowed = (
  to: RouteLocationNormalized,
  manualMode: boolean,
): boolean =>
  manualMode ||
  to.path.startsWith("/onboarding") ||
  to.query?.onboarding === "true" ||
  (to.name === PAYOUT_SETTINGS_ROUTE_NAME &&
    to.query?.onboarding === "success") ||
  (typeof to.name === "string" && LAKES_ROUTE_NAMES.has(to.name));

const handleOnboardingFlow = async (
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>,
  justCompleted: boolean,
  onboardingSuccess: boolean,
  next: NavigationGuardNext,
): Promise<boolean> => {
  if (!shouldEnforceOnboarding(to, authStore, justCompleted, onboardingSuccess)) {
    return false;
  }

  await refreshProfileIfNeeded(authStore);

  if (authStore.user?.organization_is_setup_complete) {
    return false;
  }

  const allowDuringOnboarding = isOnboardingAccessAllowed(
    to,
    isOnboardingManualMode(),
  );

  if (!allowDuringOnboarding && authStore.isManager) {
    next({ name: ONBOARDING_BASE_ROUTE_NAME });
    return true;
  }

  return false;
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // 🎯 OPTIMISATION: Amélioration du scroll behavior
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
        top: 80, // Offset pour header fixe
      };
    }
    // Scroll fluide vers le haut
    return { top: 0, behavior: "smooth" };
  },
  routes,
});

// 🔐 SÉCURITÉ: Garde de navigation globale renforcée
router.beforeEach(async (to, _, next) => {
  _pendingNavTarget = to.fullPath;
  const authStore = useAuthStore();

  try {
    if (!to.meta?.public && !authStore.user) {
      await authStore.initializeUser();
    }

    if (handleGuestOnlyRoute(to, authStore, next)) {
      return;
    }

    const onboardingSuccess = to.query?.onboarding === "success";
    const isStripeReturn = isStripeReturnRoute(to);
    const isPublicRoute = isPublicRouteCandidate(to, isStripeReturn);

    if (isStripeReturn) {
      return next({
        name: ONBOARDING_BASE_ROUTE_NAME,
        hash: STRIPE_ONBOARDING_HASH,
        query: to.query,
      });
    }

    if (!isPublicRoute && !authStore.isAuthenticated) {
      const redirectTarget = to.fullPath;
      return next({
        name: AUTH_REDIRECT_NAME,
        query: { redirect: redirectTarget },
      });
    }

    const justCompleted =
      getSessionItem(SESSION_KEYS.ONBOARDING_COMPLETED) === "1";

    if (
      await handleOnboardingFlow(
        to,
        authStore,
        justCompleted,
        onboardingSuccess,
        next,
      )
    ) {
      return;
    }

    if (!validateRouteParams(to)) {
      return next({ name: NOT_FOUND_ROUTE_NAME });
    }

    next();

    if (justCompleted) {
      removeSessionItem(SESSION_KEYS.ONBOARDING_COMPLETED);
    }
  } catch (error) {
    console.error("❌ Erreur dans beforeEach:", error);

    if (to.meta?.public) {
      return next();
    }
    return next({ name: AUTH_REDIRECT_NAME });
  }
});

// Track the intended navigation target so that when a chunk load error forces
// Vue Router to fall back to /404, we can reload the *original* URL rather
// than the fallback page.  Set in beforeEach, cleared in afterEach.
let _pendingNavTarget: string | null = null;

// Recover from stale JS chunks when Vue Router fails to load a lazy route component.
// This complements the unhandledrejection handler in main.ts for the case where
// the chunk error propagates through Vue Router rather than as an unhandled promise.
// router.onError may be absent in test environments where the router is mocked.
if (typeof router.onError === 'function') {
  router.onError((error: unknown) => {
    const isChunkLoadError =
      error instanceof TypeError &&
      /dynamically imported module|Failed to fetch dynamically|Importing a module script failed/i.test(
        (error as TypeError).message,
      );
    if (!isChunkLoadError) return;

    const RELOAD_FLAG = 'chunk_stale_reload';
    try {
      if (sessionStorage.getItem(RELOAD_FLAG)) return;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      // sessionStorage may be unavailable; allow reload anyway
    }

    // Reload the intended URL (not /404 which Vue Router may have fallen back to).
    const targetUrl = _pendingNavTarget ?? window.location.href;
    const doReload = () => window.location.replace(targetUrl);
    if (typeof caches !== 'undefined') {
      caches
        .keys()
        .then((names) => Promise.all(names.map((n) => caches.delete(n))))
        .then(doReload)
        .catch(doReload);
    } else {
      doReload();
    }
  });
}

// 🎯 OPTIMISATION: Tracking automatique des funnels avec gestion d'erreurs
router.afterEach(async (to: RouteLocationNormalized) => {
  _pendingNavTarget = null;
  // Éviter de tracker les redirections internes et les erreurs
  if (
    !to.name ||
    to.redirectedFrom ||
    to.name === NOT_FOUND_ROUTE_NAME ||
    to.name === FORBIDDEN_ROUTE_NAME
  ) {
    return;
  }

  try {
    // Chercher une configuration de funnel pour cette route
    const funnelMapping = getFunnelMappingForRoute(to.name as string);

    if (funnelMapping) {
      // Extraire les données contextuelles de la route
      const routeData = extractRouteData(to, funnelMapping);

      // Tracker l'étape du funnel
      await funnelService.trackStep({
        funnel: funnelMapping.funnel,
        step: funnelMapping.step,
        ...routeData,
      });

    }
  } catch (error) {
    // 🔐 SÉCURITÉ: Ne pas exposer d'erreurs sensibles
    if (import.meta.env.DEV) {
      console.error("Error tracking funnel step:", error);
    }
  }

  // Track page view in PostHog (no-op if not consented or not initialized)
  posthogService.trackPageView(to.fullPath)

  // GTM: enriched page view with title (supplements vue-gtm automatic tracking)
  gtmService.trackPageView(to.fullPath)
});


export default router;
